import {
	ReactElement,
	useCallback,
	useEffect,
	useState
} from "react";
import { setTrackingId } from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	AnnouncementGlobalSearchResultItem,
	AppGlobalSearchResultItem,
	ContactGlobalSearchResultItem,
	ContactSearchSource,
	DocumentGlobalSearchResultItem,
	EgnyteGlobalSearchResultItem,
	EmailGlobalSearchResultItem,
	EventGlobalSearchResultItem,
	GlobalFileSearchResultModel,
	SearchIntegrationKind,
	GlobalSearchScopeIdentifier,
	GlobalSearchScopeKind,
	IntegrationFlowGlobalSearchResultItem,
	KnowledgeItemGlobalSearchResultItem,
	ScopeSearchNodeModel,
	SharePointSearchTimeScope,
	SharePointSearchValue,
	UserSortBy,
	getIsFullTextSearchEnabled,
	searchAnnouncements,
	searchApps,
	searchContacts,
	searchEgnyte,
	searchEmail,
	searchEvents,
	searchFiles,
	searchFolders,
	searchIntegration,
	searchKnowledgeItems,
	searchCommunities,
	CommunityGlobalSearchResultItem,
	CommunityPostGlobalSearchResultItem,
	searchCommunityPosts
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import {
	MediaQueryKind,
	useForceRerender,
	useUserTimezone,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { ScopeLayout } from "../../ScopeLayout";
import { NoResults, Spinner } from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import styles from "./MultiScopeSearch.module.less";
import { CategoryView } from "./CategoryView";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { never } from "client_legacy/ndaw/errors";
import { SortDirection } from "client/Api/common";
import {
	EmailDateFilterKind,
	EmailSortDirection,
	EmailSortOrder
} from "client/Api/Email/email";
import { AnnouncementSortProperty } from "client/Api/Hub/Announcements/announcementViewer";
import { KnowledgeItemSortProperty } from "client/Api/Hub/KnowledgeItems/knowledgeItemViewer";
import { EventDateFilterType, EventDateSortDirection } from "client/Api/Hub/Events/eventViewer";
import { SharePointColumnId } from "client/Api/Documents/SharePoint/common";
import { TimeZone } from "client/DateAndTime/timezones";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import { normalizeSearchScopeIdentifier } from "client/Features/Layout/TitleBar/GlobalSearch/useSearchResultsScopeTabs";
import { convertToIdentifier } from "client/Features/Layout/TitleBar/GlobalSearch/useFetchGlobalSearchResults";
import { useContactDialog } from "client/Features/AddressBook/ContactDialog";
import { ContactSource } from "client/Api/Email/contacts";

export type MultiScopeSearchProps = {
	scopes: readonly ScopeSearchNodeModel[];
	searchTerm: VersionedValue<string>;
	onViewMore: (scope: GlobalSearchScopeIdentifier) => void;
};

export const MultiScopeSearch = (props: MultiScopeSearchProps): ReactElement => {
	const {
		scopes,
		searchTerm,
		onViewMore
	} = props;

	const [reload, loadVersion] = useForceRerender();
	const [resultState, setResultState] = useState<ResultData>({
		results: [],
		status: ResultStatus.Success,
	});

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const userTimezone: TimeZone = useUserTimezone();
	const maxItemsPerScopeCount: number = getMaxItemsPerScope(scopes.length);
	const [contactDialog, openContactDialog] = useContactDialog();

	const startLoading = useCallback(
		(): void => {
			setResultState({
				status: ResultStatus.Loading,
				results: []
			});
		},
		[]
	);

	const finishLoading = useCallback(
		(wasSuccessful: boolean): void => {
			setResultState(resultState => {
				if (resultState.status !== ResultStatus.Loading) {
					throw new Error(`Cannot finish status '${resultState.status}'`);
				}

				const newResultState: ResultData = wasSuccessful
					? {
						...resultState,
						status: ResultStatus.Success
					}
					: {
						status: ResultStatus.Error
					};

				return newResultState;
			});
		},
		[]
	);

	const appendResult = useCallback(
		(value: SearchCategoryResult): void => {
			setResultState(resultState => {
				if (resultState.status === ResultStatus.Error) {
					throw new Error("Cannot append to an error");
				}

				return {
					...resultState,
					results: [...resultState.results, value]
				};
			});
		},
		[]
	);

	useEffect(
		(): (() => void) | void => {
			hasImplicitDependencyOn(loadVersion);
			const cancellation = new AbortController();

			fetchResults({
				searchTerm: searchTerm.value,
				availableScopes: scopes,
				signal: cancellation.signal,
				maxItemsPerScopeCount,
				onSetResult: setResultState,
				onLoadingStart: startLoading,
				finishLoading,
				appendResult,
				onCancel: () => cancellation.abort(),
			});

			return () => cancellation.abort();
		},
		[
			scopes,
			searchTerm,
			loadVersion,
			maxItemsPerScopeCount,
			appendResult,
			finishLoading,
			startLoading
		]
	);

	const openContactDetailsHandler = useCallback(
		(id: string, source: ContactSource): void => {
			if (source === ContactSource.Suggested) {
				throw new Error("Address book does not support suggested contacts");
			}

			openContactDialog({
				source,
				initialView: {
					kind: "details",
					contactId: id
				}
			});
		},
		[openContactDialog]
	);

	const renderContent = (): ReactElement => {
		if (resultState.status === ResultStatus.Error) {
			return (
				<NoResults
					trackingId="has-error"
					icon="gear"
					title={localize("_NotAbleToRetrieveSearchResults")}
					message={localize("_WeWereUnableToRetrieveSearchResults")}
					onRetry={reload}
					height="fill"
				/>
			);
		}

		if (resultState.status === ResultStatus.Success && resultState.results.length === 0) {
			return (
				<NoResults
					trackingId="no-results-found"
					icon="magnifying-glass"
					title={localize("_NoSearchResults")}
					message={localize("_PleaseTrySearchingWithAnotherTerm")}
					height="fill"
				/>
			);
		}

		let itemsCount: number = 0;

		return (
			<>
				{(resultState.status === ResultStatus.Loading || resultState.status === ResultStatus.Success) && (
					resultState.results.map(result => {
						const navigationStartIndex: number = itemsCount;

						if (result.status === SearchCategoryResultStatus.Success) {
							itemsCount = itemsCount + result.items.length;

							if (result.hasMore) {
								itemsCount++;
							}
						}

						return (
							<CategoryView
								key={normalizeSearchScopeIdentifier(result.itemsScope)}
								result={result}
								isMobile={isMobile}
								navigationStartIndex={navigationStartIndex}
								timezone={userTimezone}
								onViewMore={onViewMore}
								onOpenContactDetails={openContactDetailsHandler}
							/>
						);
					})
				)}
				{resultState.status === ResultStatus.Loading && (
					<div
						{...setTrackingId("busy-indicator")}
						className={styles.loadingIndicator}
					>
						<Spinner />
					</div>
				)}
			</>
		);
	};

	return (
		<>
			<ScopeLayout>
				<ScopeLayout.Results>
					{renderContent()}
				</ScopeLayout.Results>
			</ScopeLayout>
			{contactDialog}
		</>
	);
};

MultiScopeSearch.displayName = "MultiScopeSearch";

enum ResultStatus {
	Success = 0,
	Loading = 1,
	Error = 2
}

type ResultData = {
	status: ResultStatus.Success | ResultStatus.Loading;
	results: readonly SearchCategoryResult[];
} | {
	status: ResultStatus.Error;
};

type FetchResultOptions = {
	searchTerm: string;
	availableScopes: readonly ScopeSearchNodeModel[];
	signal: AbortSignal;
	maxItemsPerScopeCount: number;
	onSetResult: (result: ResultData) => void;
	onLoadingStart(): void;
	finishLoading(wasSuccessful: boolean): void;
	appendResult(value: SearchCategoryResult): void;
	onCancel: () => void;
};

async function fetchResults(options: FetchResultOptions): Promise<void> {
	const {
		searchTerm,
		availableScopes,
		signal,
		maxItemsPerScopeCount,
		onSetResult,
		onLoadingStart,
		finishLoading,
		appendResult,
		onCancel
	} = options;

	if (!searchTerm.trim()) {
		onSetResult({
			status: ResultStatus.Success,
			results: [],
		});
		return;
	}

	onLoadingStart();

	const scopesToQuery: readonly ScopeSearchNodeModel[] = sortBy(
		availableScopes,
		x => x.usageSortOrder
	);

	let numberOfRequestFailures: number = 0;

	const fetchForScope = async (scopeToQuery: ScopeSearchNodeModel): Promise<SearchCategoryResult | null> => {
		const { name } = scopeToQuery;
		const scopeIdentifier: GlobalSearchScopeIdentifier = convertToIdentifier(scopeToQuery);

		try {
			const items = await fetchItems(
				searchTerm,
				scopeIdentifier,
				maxItemsPerScopeCount + 1,
				signal
			);

			return {
				itemsScope: scopeIdentifier,
				name,
				status: SearchCategoryResultStatus.Success,
				items: items.slice(0, maxItemsPerScopeCount),
				hasMore: items.length > maxItemsPerScopeCount
			};
		} catch (e) {
			if (isCancelledError(e)) {
				return null;
			}

			numberOfRequestFailures++;

			return {
				itemsScope: scopeIdentifier,
				name,
				status: SearchCategoryResultStatus.Failed,
				items: null,
				hasMore: false
			};
		}
	};

	const promises: Promise<SearchCategoryResult | null>[] = scopesToQuery.map(
		scope => fetchForScope(scope)
	);

	const fallBackPromises: Promise<SearchCategoryResult | null>[] = promises.splice(
		MAX_NUMBER_OF_SCOPES_IN_TOP_RESULT
	);

	const processPendingRequest = async (
		promise: Promise<SearchCategoryResult | null>
	): Promise<void> => {
		const result: SearchCategoryResult | null = await promise;

		if (signal.aborted) {
			return;
		}

		let wasAppended = false;
		if (result !== null) {
			switch (result.status) {
				case SearchCategoryResultStatus.Success:
					if (result.items.length > 0) {
						appendResult(result);
						wasAppended = true;
					}
					break;
				case SearchCategoryResultStatus.Failed:
					break;
				default:
					never(result);
			}
		}

		if (!wasAppended) {
			const nextToProcess: Promise<SearchCategoryResult | null> | undefined = fallBackPromises.shift();

			if (nextToProcess) {
				await processPendingRequest(nextToProcess);
			}
		}
	};

	await Promise.allSettled(promises.map(processPendingRequest));

	if (!signal.aborted) {
		finishLoading(numberOfRequestFailures < scopesToQuery.length);
		onCancel();
	}

	await Promise.allSettled(fallBackPromises);
}

export const enum SearchCategoryResultStatus {
	Success = 1,
	Failed = 2
};

export type SearchCategoryResult = {
	name: string;
	itemsScope: GlobalSearchScopeIdentifier;
} & (
		{
			status: SearchCategoryResultStatus.Success;
			items: readonly ISearchResultItem[];
			hasMore: boolean;
		}
		|
		{
			status: SearchCategoryResultStatus.Failed;
			items: null;
			hasMore: false;
		}
	);

async function fetchItems(
	searchTerm: string,
	scope: GlobalSearchScopeIdentifier,
	maxItems: number,
	signal: AbortSignal
): Promise<readonly ISearchResultItem[]> {
	switch (scope.kind) {
		case GlobalSearchScopeKind.Predefined:
			return await fetchItemsOfPredefinedIntegration(
				searchTerm,
				scope.predefinedKind,
				maxItems,
				signal
			);
		case GlobalSearchScopeKind.IntegrationFlow:
			return await searchIntegration(
				scope.integrationId,
				{
					searchTerm,
					maxAmount: maxItems
				},
				signal
			);
		default:
			never(scope);
	}
}

async function fetchItemsOfPredefinedIntegration(
	searchTerm: string,
	predefinedKind: SearchIntegrationKind,
	maxItems: number,
	signal: AbortSignal
): Promise<readonly ISearchResultItem[]> {
	switch (predefinedKind) {
		case SearchIntegrationKind.Apps:
			return await searchApps(
				{
					searchTerm,
					categories: [],
					sortDirection: SortDirection.Ascending,
					take: maxItems
				},
				signal
			);
		case SearchIntegrationKind.Files:
			const {
				items: fileItems
			} = await searchFiles(
				{
					searchTerm,
					documentKinds: [],
					order: {
						property: SharePointColumnId.name,
						direction: SortDirection.Ascending
					},
					searchIn: SharePointSearchValue.All,
					skip: 0,
					take: maxItems,
					timeScope: SharePointSearchTimeScope.AnyTime
				},
				signal
			);

			return fileItems;
		case SearchIntegrationKind.Emails:
			const {
				results: {
					items: emailItems
				}
			} = await searchEmail(
				{
					searchTerm,
					dateFilterKind: EmailDateFilterKind.Any,
					filterFolderId: {isSet: false},
					filterSelectors: [],
					skip: 0,
					sortDirection: EmailSortDirection.Descending,
					sortOrder: EmailSortOrder.Date,
					take: maxItems
				},
				signal
			);

			return emailItems;
		case SearchIntegrationKind.Contacts:
			const {
				lazyLoadPageResult: {
					items: contactItems
				}
			} = await searchContacts(
				{
					searchTerm,
					departments: [],
					jobTitles: [],
					officeLocations: [],
					skip: 0,
					source: ContactSearchSource.All,
					take: maxItems,
					sortBy: UserSortBy.Default
				},
				signal
			);

			return contactItems;
		case SearchIntegrationKind.Announcements:
			const {
				items: announcementItems
			} = await searchAnnouncements(
				{
					searchTerm,
					afterAnnouncement: {isSet: false},
					authorIds: [],
					categoryIds: [],
					sortOrder: {
						property: AnnouncementSortProperty.StartTime,
						direction: SortDirection.Descending
					},
					amount: maxItems
				},
				signal
			);

			return announcementItems;
		case SearchIntegrationKind.KnowledgeItems:
			const isFullTextSearchEnabled: boolean = await getIsFullTextSearchEnabled(signal);

			const {
				items: knowledgeItems
			} = await searchKnowledgeItems(
				{
					searchTerm,
					authorIds: [],
					categoryIds: [],
					amount: maxItems,
					pagingInformation: {isSet: false},
					sortOrder: {
						property: isFullTextSearchEnabled
							? KnowledgeItemSortProperty.Relevance
							: KnowledgeItemSortProperty.Views,
						direction: SortDirection.Descending
					}
				},
				signal
			);

			return knowledgeItems;
		case SearchIntegrationKind.Egnyte:
			const {
				items: EgnyteItems
			} = await searchEgnyte(
				{
					searchTerm,
					take: maxItems,
					order: null,
					timeScope: SharePointSearchTimeScope.AnyTime,
					skip: 0
				}
			);

			return EgnyteItems;
		case SearchIntegrationKind.Events:
			const {
				items: eventItems
			} = await searchEvents(
				{
					searchTerm,
					amount: maxItems,
					categoryIds: [],
					dateFilterType: EventDateFilterType.Upcoming,
					locationTypes: [],
					pagingInformation: {isSet: false},
					sortDateDirection: EventDateSortDirection.NearToFar
				},
				signal
			);

			return eventItems;
		case SearchIntegrationKind.Folders:
			const {
				items: folderItems
			} = await searchFolders(
				{
					searchTerm,
					order: null,
					skip: 0,
					take: maxItems,
					timeScope: SharePointSearchTimeScope.AnyTime
				},
				signal
			);

			return folderItems;
		case SearchIntegrationKind.Communities:
			const {
				items
			} = await searchCommunities(
				{
					searchTerm,
					shouldBeMember: { isSet: false },
					sortDirection: SortDirection.Ascending,
					pagingInfo: { isSet: false },
					amount: maxItems
				},
				signal
			);

			return items;
		case SearchIntegrationKind.CommunityPosts:
			const {
				items: communityPosts
			} = await searchCommunityPosts(
				{
					searchTerm,
					sortDirection: SortDirection.Ascending,
					amount: maxItems,
					pagingInfo: { isSet: false }
				},
				signal
			);

			return communityPosts;
		default:
			throw never(predefinedKind);
	}
}

export function getMaxItemsPerScope(
	scopeCount: number
): number {
	switch(scopeCount) {
		case 1:
			return 10;
		case 2:
			return 5;
		default:
			return DEFAULT_MAX_ITEMS_FOR_SCOPE;
	}
}

export type ISearchResultItem =
	| AppGlobalSearchResultItem
	| GlobalFileSearchResultModel
	| DocumentGlobalSearchResultItem
	| EmailGlobalSearchResultItem
	| ContactGlobalSearchResultItem
	| AnnouncementGlobalSearchResultItem
	| KnowledgeItemGlobalSearchResultItem
	| EgnyteGlobalSearchResultItem
	| EventGlobalSearchResultItem
	| IntegrationFlowGlobalSearchResultItem
	| CommunityGlobalSearchResultItem
	| CommunityPostGlobalSearchResultItem;

const MAX_NUMBER_OF_SCOPES_IN_TOP_RESULT = 5;
const DEFAULT_MAX_ITEMS_FOR_SCOPE: number = 3;